(() => {
const typekitId = 'axis-mundi-typekit';

function loadAxisMundiTypekit() {
  if (document.getElementById(typekitId)) return;

  const stylesheet = document.createElement('link');
  stylesheet.id = typekitId;
  stylesheet.rel = 'stylesheet';
  stylesheet.href = 'https://use.typekit.net/qdb8bjf.css';
  document.head.appendChild(stylesheet);
}

class AxisMundiWorld {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.isCustomerAuthenticated = section.dataset.customerAuthenticated === 'true';
    this.customerEmail = section.dataset.customerEmail || '';
    this.isServerTrialOneComplete = section.dataset.serverTrialOneComplete === 'true';
    this.progressEndpoint = section.dataset.axisProgressEndpoint || '';
    this.isGateBypassed = section.dataset.accountGateBypassed === 'true';
    this.canEnterWorld = this.isCustomerAuthenticated || this.isGateBypassed;
    this.storageKey = `axis-mundi-world:${this.sectionId}`;
    this.legacyEntryKey = `album-landing-entered:${this.sectionId}`;
    this.authDestinationKey = `axis-mundi-auth-destination:${this.sectionId}`;
    this.accountLoginUrl = section.dataset.accountLoginUrl;
    this.views = Array.from(section.querySelectorAll('[data-axis-view]'));
    this.modal = section.querySelector('[data-axis-gate-modal]');
    this.introTransition = section.querySelector('[data-axis-intro-transition]');
    this.introLine = section.querySelector('[data-axis-intro-line]');
    this.chapterOneCard = section.querySelector('[data-axis-trial-card="trial-1"]');
    this.chapterOneStatus = section.querySelector('[data-axis-chapter-one-status]');
    this.vaultDestination = section.querySelector('[data-axis-vault-destination]');
    this.vaultDestinationLabel = section.querySelector('[data-axis-vault-destination-label]');
    this.vaultDestinationStatus = section.querySelector('[data-axis-vault-destination-status]');
    this.vaultChapterOneStatus = section.querySelector('[data-axis-vault-trial-one-status]');
    this.vaultChapterOneLocked = section.querySelector('[data-axis-vault-trial-one-locked]');
    this.vaultChapterOneUnlocked = section.querySelector('[data-axis-vault-trial-one-unlocked]');
    this.vaultVideo = section.querySelector('[data-axis-vault-video]');
    this.vaultVideoSource = section.querySelector('[data-axis-vault-video-source]');
    this.vaultVideoTitle = section.querySelector('[data-axis-vault-video-title]');
    this.vaultVideoOptions = Array.from(section.querySelectorAll('[data-axis-vault-video-option]'));
    this.prologueModal = section.querySelector('[data-axis-prologue-modal]');
    this.prologueAudio = section.querySelector('[data-axis-prologue-audio]');
    this.prologueLines = Array.from(section.querySelectorAll('[data-axis-prologue-line]'));
    this.prologueTrigger = null;
    this.introTransitionActive = false;
    this.state = this.readState();
    const shouldMigrateTrialOne = (
      this.isCustomerAuthenticated
      && this.customerEmail
      && this.hasCompletedTrial('trial-1')
      && !this.isServerTrialOneComplete
    );
    if (this.isServerTrialOneComplete) this.completeTrial('trial-1');
    this.restoreAuthDestination();
    this.saveState();

    this.bindEvents();
    this.readChapterOneCompletion();
    this.syncTrialState();
    if (shouldMigrateTrialOne) this.syncTrialOneCompletion('local_storage_migration');

    this.showView(this.getInitialView(), false);
  }

  bindEvents() {
    this.section.querySelectorAll('[data-axis-enter-trigger]').forEach((button) => {
      button.addEventListener('click', () => {
        if (this.canEnterWorld) {
          this.enterWorld();
          return;
        }

        this.openGate();
      });
    });

    this.section.querySelectorAll('[data-axis-account-login]').forEach((link) => {
      link.addEventListener('click', () => this.rememberAuthDestination());
    });

    this.section.querySelectorAll('[data-axis-open-chapter="chapter-1"]').forEach((button) => {
      button.addEventListener('click', () => this.showView('chapter-1'));
    });

    this.section.querySelectorAll('[data-axis-open-rewards]').forEach((button) => {
      button.addEventListener('click', () => {
        if (this.hasCompletedTrial('trial-1')) this.showView('rewards');
      });
    });

    this.section.querySelectorAll('[data-axis-back-to-chapters]').forEach((button) => {
      button.addEventListener('click', () => this.showView('chapter-select'));
    });

    this.section.querySelectorAll('[data-axis-open-prologue]').forEach((button) => {
      button.addEventListener('click', () => this.openPrologue(button));
    });

    this.section.querySelectorAll('[data-axis-close-prologue]').forEach((button) => {
      button.addEventListener('click', () => this.closePrologue());
    });

    if (this.prologueAudio) {
      this.prologueAudio.addEventListener('timeupdate', () => this.syncPrologueLine());
      this.prologueAudio.addEventListener('seeked', () => this.syncPrologueLine());
      this.prologueAudio.addEventListener('ended', () => this.syncPrologueLine());
    }

    if (this.prologueModal) {
      this.prologueModal.addEventListener('click', (event) => {
        if (event.target === this.prologueModal) this.closePrologue();
      });
    }

    this.vaultVideoOptions.forEach((button, index) => {
      button.addEventListener('click', () => this.selectVaultVideo(button));
      button.addEventListener('keydown', (event) => this.handleVaultVideoKeydown(event, index));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.prologueModal && !this.prologueModal.hidden) {
        this.closePrologue();
      }
    });

    this.section.addEventListener('axis:chapter-complete', (event) => {
      if (event.detail && event.detail.chapter === 'chapter-1') {
        const newlyCompleted = this.completeTrial('trial-1');
        this.saveState();
        this.syncTrialState();
        if (newlyCompleted) this.syncTrialOneCompletion('live');
      }
    });

    document.addEventListener('shopify:block:select', (event) => {
      if (!this.section.contains(event.target)) return;
      this.showView('chapter-1', false);
    });
  }

  readState() {
    const defaults = {
      version: 2,
      entered: false,
      currentView: 'entry',
      completedTrials: [],
    };
    const validViews = ['entry', 'chapter-select', 'chapter-1', 'rewards'];
    const knownTrials = ['trial-1', 'trial-2', 'trial-3', 'trial-4'];

    try {
      const saved = JSON.parse(window.localStorage.getItem(this.storageKey));
      if (saved && typeof saved === 'object') {
        const completedTrials = Array.isArray(saved.completedTrials) ? saved.completedTrials : [];
        if (saved.chapterOneComplete === true) completedTrials.push('trial-1');

        const normalizedView = saved.currentView === 'chapter-1-complete' ? 'chapter-1' : saved.currentView;
        return {
          version: 2,
          entered: Boolean(saved.entered),
          currentView: validViews.includes(normalizedView) ? normalizedView : defaults.currentView,
          completedTrials: completedTrials.filter((trial, index, trials) => (
            knownTrials.includes(trial) && trials.indexOf(trial) === index
          )),
        };
      }
    } catch (error) {
      // Ignore corrupt localStorage and use defaults.
    }

    try {
      if (window.localStorage.getItem(this.legacyEntryKey) === 'true') {
        defaults.entered = true;
        defaults.currentView = 'chapter-select';
      }
    } catch (error) {
      // Ignore unavailable localStorage.
    }

    return defaults;
  }

  saveState() {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch (error) {
      // The experience should remain usable even when localStorage is unavailable.
    }
  }

  readChapterOneCompletion() {
    const chapter = this.section.querySelector('[data-axis-chapter]');
    if (chapter && chapter.dataset.complete === 'true') {
      this.completeTrial('trial-1');
      this.saveState();
    }
  }

  hasCompletedTrial(trialId) {
    return this.state.completedTrials.includes(trialId);
  }

  completeTrial(trialId) {
    if (this.hasCompletedTrial(trialId)) return false;
    this.state.completedTrials.push(trialId);
    return true;
  }

  syncTrialOneCompletion(source) {
    if (!this.isCustomerAuthenticated || !this.customerEmail || !this.progressEndpoint) return;

    window.fetch(`${this.progressEndpoint}/v1/completions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: this.customerEmail,
        trial: 'trial-1',
        source,
      }),
    }).catch(() => {});
  }

  getInitialView() {
    if (!this.canEnterWorld) return 'entry';

    if (!this.state.entered) return 'entry';

    const savedView = this.state.currentView;
    if (savedView === 'chapter-1') {
      return savedView;
    }

    if (savedView === 'rewards' && this.hasCompletedTrial('trial-1')) return savedView;

    return 'chapter-select';
  }

  openGate() {
    if (!this.modal) {
      this.rememberAuthDestination();
      if (this.accountLoginUrl) window.location.assign(this.accountLoginUrl);
      return;
    }

    this.modal.hidden = false;
    document.body.classList.add('axis-world-gate-open');
  }

  rememberAuthDestination() {
    try {
      window.sessionStorage.setItem(this.authDestinationKey, 'chapter-select');
    } catch (error) {
      // Shopify's return_to parameter remains the primary return mechanism.
    }
  }

  restoreAuthDestination() {
    if (!this.isCustomerAuthenticated) return;

    const url = new URL(window.location.href);
    let destination = url.searchParams.get('axis_destination');

    try {
      destination = destination || window.sessionStorage.getItem(this.authDestinationKey);
      window.sessionStorage.removeItem(this.authDestinationKey);
    } catch (error) {
      // Continue with the return_to marker when sessionStorage is unavailable.
    }

    if (destination === 'chapter-select') {
      this.state.entered = true;
      this.state.currentView = 'chapter-select';
    }

    if (url.searchParams.has('axis_destination')) {
      url.searchParams.delete('axis_destination');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }

  openPrologue(trigger) {
    if (!this.prologueModal || !this.prologueAudio) return;

    this.prologueTrigger = trigger;
    this.prologueModal.hidden = false;
    document.body.classList.add('axis-world-prologue-open');
    this.prologueAudio.currentTime = 0;
    this.syncPrologueLine();
    this.prologueModal.querySelector('[data-axis-close-prologue]')?.focus();

    const playPromise = this.prologueAudio.play();
    if (playPromise) playPromise.catch(() => {});
  }

  closePrologue() {
    if (!this.prologueModal || !this.prologueAudio) return;

    this.prologueAudio.pause();
    this.prologueModal.hidden = true;
    document.body.classList.remove('axis-world-prologue-open');
    this.prologueLines.forEach((line) => line.classList.remove('is-active'));
    this.prologueTrigger?.focus();
  }

  syncPrologueLine() {
    if (!this.prologueAudio || this.prologueLines.length === 0) return;

    const currentTime = this.prologueAudio.currentTime;
    let activeLine = this.prologueLines[0];

    this.prologueLines.forEach((line) => {
      if (currentTime >= Number(line.dataset.start)) activeLine = line;
    });

    this.prologueLines.forEach((line) => {
      line.classList.toggle('is-active', line === activeLine);
    });
  }

  selectVaultVideo(button) {
    if (!this.vaultVideo || !this.vaultVideoSource || !button) return;

    const videoUrl = button.dataset.videoUrl;
    const posterUrl = button.dataset.videoPoster;
    const label = button.dataset.videoLabel;
    if (!videoUrl || !posterUrl || !label) return;

    const sourceChanged = this.vaultVideoSource.getAttribute('src') !== videoUrl;
    if (sourceChanged) {
      this.vaultVideo.pause();
      this.vaultVideoSource.setAttribute('src', videoUrl);
      this.vaultVideo.setAttribute('poster', posterUrl);
      this.vaultVideo.setAttribute('aria-label', label);
      this.vaultVideo.load();
    }

    if (this.vaultVideoTitle) this.vaultVideoTitle.textContent = label;
    this.vaultVideoOptions.forEach((option) => {
      const isActive = option === button;
      option.classList.toggle('is-active', isActive);
      option.setAttribute('aria-pressed', String(isActive));
      option.tabIndex = isActive ? 0 : -1;
    });
  }

  handleVaultVideoKeydown(event, index) {
    const lastIndex = this.vaultVideoOptions.length - 1;
    let nextIndex = null;

    if (event.key === 'ArrowRight') nextIndex = index === lastIndex ? 0 : index + 1;
    if (event.key === 'ArrowLeft') nextIndex = index === 0 ? lastIndex : index - 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = lastIndex;
    if (nextIndex === null) return;

    event.preventDefault();
    const nextButton = this.vaultVideoOptions[nextIndex];
    this.selectVaultVideo(nextButton);
    nextButton.focus();
  }

  enterWorld() {
    if (!this.canEnterWorld) {
      this.openGate();
      return;
    }

    if (this.introTransitionActive) return;

    this.state.entered = true;
    this.state.currentView = 'chapter-select';
    this.saveState();

    this.playIntroTransition(() => {
      this.showView('chapter-select');
    });
  }

  playIntroTransition(onComplete) {
    const message = 'The tale begins, and so does thy trial';

    if (!this.introTransition || !this.introLine) {
      onComplete();
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const enterButtons = Array.from(this.section.querySelectorAll('[data-axis-enter-trigger]'));
    this.introTransitionActive = true;
    enterButtons.forEach((button) => {
      button.disabled = true;
    });

    this.introLine.textContent = '';
    this.introTransition.hidden = false;

    if (reduceMotion) {
      this.introLine.textContent = message;
      window.setTimeout(() => {
        this.introTransitionActive = false;
        this.introTransition.hidden = true;
        onComplete();
      }, 900);
      return;
    }

    let index = 0;
    const typeNextCharacter = () => {
      this.introLine.textContent = message.slice(0, index);

      if (index >= message.length) {
        window.setTimeout(() => {
          this.introTransitionActive = false;
          this.introTransition.hidden = true;
          onComplete();
        }, 450);
        return;
      }

      index += 1;
      window.setTimeout(typeNextCharacter, 48);
    };

    typeNextCharacter();
  }

  showView(viewName, save = true) {
    if (viewName !== 'rewards') this.vaultVideo?.pause();

    this.views.forEach((view) => {
      view.hidden = view.dataset.axisView !== viewName;
    });

    this.section.dataset.axisState = viewName;

    if (save) {
      this.state.currentView = viewName;
      this.saveState();
    }
  }

  syncTrialState() {
    const isComplete = this.hasCompletedTrial('trial-1');

    this.chapterOneCard?.classList.toggle('is-available', !isComplete);
    this.chapterOneCard?.classList.toggle('is-complete', isComplete);
    if (this.chapterOneStatus) this.chapterOneStatus.textContent = isComplete ? 'Complete' : 'Available';
    if (this.vaultDestination) {
      this.vaultDestination.disabled = !isComplete;
      this.vaultDestination.setAttribute('aria-disabled', String(!isComplete));
      this.vaultDestination.classList.toggle('is-sealed', !isComplete);
      this.vaultDestination.classList.toggle('is-unsealed', isComplete);
    }
    if (this.vaultDestinationLabel) {
      this.vaultDestinationLabel.textContent = isComplete ? 'Enter the Vault →' : 'Rewards Vault';
    }
    if (this.vaultDestinationStatus) {
      this.vaultDestinationStatus.hidden = isComplete;
      if (!isComplete) this.vaultDestinationStatus.textContent = 'Sealed';
    }
    if (this.vaultChapterOneStatus) this.vaultChapterOneStatus.textContent = isComplete ? 'Complete' : 'Sealed';
    if (this.vaultChapterOneLocked) this.vaultChapterOneLocked.hidden = isComplete;
    if (this.vaultChapterOneUnlocked) this.vaultChapterOneUnlocked.hidden = !isComplete;
  }
}

function initAxisMundiWorld(container = document) {
  loadAxisMundiTypekit();

  container.querySelectorAll('[data-axis-world]').forEach((section) => {
    if (section.axisMundiWorld) return;
    section.axisMundiWorld = new AxisMundiWorld(section);
  });
}

document.addEventListener('DOMContentLoaded', () => initAxisMundiWorld());
document.addEventListener('shopify:section:load', (event) => initAxisMundiWorld(event.target));
})();
